import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation } from 'react-router-dom';
import MainSidebar from '../../components/ui/MainSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { customerFeedbackService } from '../../services/customerFeedbackService';
import { workOrderService } from '../../services/workOrderService';
import { technicianService } from '../../services/technicianService';
import { useAuth } from '../../contexts/AuthContext';

const CustomerFeedback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // Work order state
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [loadingWorkOrders, setLoadingWorkOrders] = useState(true);
  
  // Feedback form state
  const [satisfactionRating, setSatisfactionRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [serviceComments, setServiceComments] = useState('');
  const [followUpRequested, setFollowUpRequested] = useState(false);
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpTime, setFollowUpTime] = useState('');
  const [preferredServiceType, setPreferredServiceType] = useState('');
  const [preferredTechnicianId, setPreferredTechnicianId] = useState('');
  
  // Technicians state
  const [technicians, setTechnicians] = useState([]);
  
  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [characterCount, setCharacterCount] = useState(0);

  useEffect(() => {
    fetchDeliveredWorkOrders();
    fetchTechnicians();
    
    // Load from navigation state if available
    if (location?.state?.workOrder) {
      setSelectedWorkOrder(location?.state?.workOrder);
    }
  }, [location]);

  useEffect(() => {
    setCharacterCount(serviceComments?.length || 0);
  }, [serviceComments]);

  const fetchDeliveredWorkOrders = async () => {
    try {
      setLoadingWorkOrders(true);
      const orders = await workOrderService?.getAll();
      const deliveredOrders = orders?.filter(order => order?.estadoOt === 'entregado');
      setWorkOrders(deliveredOrders || []);
    } catch (error) {
      console.error('Error fetching work orders:', error);
      setError('Error al cargar órdenes de trabajo');
    } finally {
      setLoadingWorkOrders(false);
    }
  };

  const fetchTechnicians = async () => {
    try {
      const techs = await technicianService?.getAll();
      setTechnicians(techs || []);
    } catch (error) {
      console.error('Error fetching technicians:', error);
    }
  };

  const handleWorkOrderSelect = (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setError('');
  };

  const handleStarClick = (rating) => {
    setSatisfactionRating(rating);
    setError('');
  };

  const handleStarHover = (rating) => {
    setHoveredRating(rating);
  };

  const handleStarLeave = () => {
    setHoveredRating(0);
  };

  const validateForm = () => {
    if (!selectedWorkOrder) {
      setError('Seleccione una orden de trabajo');
      return false;
    }

    if (satisfactionRating === 0) {
      setError('Seleccione una calificación de satisfacción');
      return false;
    }

    if (followUpRequested) {
      if (!followUpDate) {
        setError('Ingrese la fecha de seguimiento');
        return false;
      }
      if (!followUpTime) {
        setError('Ingrese la hora de seguimiento');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      setError('');

      const feedbackData = {
        workOrderId: selectedWorkOrder?.id,
        satisfactionRating: satisfactionRating,
        serviceComments: serviceComments || null,
        followUpRequested: followUpRequested,
        followUpDate: followUpRequested ? followUpDate : null,
        followUpTime: followUpRequested ? followUpTime : null,
        preferredServiceType: followUpRequested ? preferredServiceType : null,
        preferredTechnicianId: followUpRequested && preferredTechnicianId ? preferredTechnicianId : null
      };

      await customerFeedbackService?.create(feedbackData);

      // Create timeline event
      await workOrderService?.createTimelineEvent({
        workOrderId: selectedWorkOrder?.id,
        stage: 'feedback',
        status: 'Feedback del cliente recibido',
        findings: `Calificación: ${satisfactionRating}/5 estrellas. ${followUpRequested ? 'Seguimiento solicitado.' : ''}`
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/workshop-dashboard');
      }, 2000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setError('Error al enviar el feedback. Intente nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRatingLabel = (rating) => {
    const labels = {
      1: 'Muy insatisfecho',
      2: 'Insatisfecho',
      3: 'Neutral',
      4: 'Satisfecho',
      5: 'Muy satisfecho'
    };
    return labels?.[rating] || '';
  };

  const displayRating = hoveredRating || satisfactionRating;

  return (
    <>
      <Helmet>
        <title>Feedback del Cliente - Gearcontrol</title>
      </Helmet>
      <div className="flex h-screen bg-background">
        <MainSidebar isCollapsed={sidebarCollapsed} userRole={userProfile?.role} />
        
        <main className={`flex-1 overflow-y-auto transition-smooth ${sidebarCollapsed ? 'ml-20' : 'ml-64'}`}>
          <div className="container mx-auto px-6 py-8 max-w-5xl">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <Icon name="MessageSquare" size={28} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-3xl font-heading font-bold text-foreground">
                    Feedback del Cliente
                  </h1>
                  <p className="text-muted-foreground mt-1">
                    Evaluación de satisfacción post-entrega
                  </p>
                </div>
              </div>
            </div>

            {/* Success Message */}
            {success && (
              <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-center gap-3">
                <Icon name="CheckCircle" size={20} className="text-success" />
                <p className="text-success font-medium">Feedback enviado exitosamente. Redirigiendo...</p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-3">
                <Icon name="AlertCircle" size={20} className="text-destructive" />
                <p className="text-destructive font-medium">{error}</p>
              </div>
            )}

            {/* Work Order Selection */}
            <div className="bg-card rounded-lg shadow-elevation-2 p-6 mb-6">
              <h2 className="text-xl font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="FileText" size={20} className="text-primary" />
                Seleccionar Orden de Trabajo
              </h2>

              {loadingWorkOrders ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : workOrders?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Icon name="Package" size={48} className="mx-auto mb-3 opacity-50" />
                  <p>No hay órdenes de trabajo entregadas disponibles</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {workOrders?.map((order) => (
                    <button
                      key={order?.id}
                      onClick={() => handleWorkOrderSelect(order)}
                      className={`p-4 rounded-lg border-2 transition-smooth text-left hover-lift press-effect ${
                        selectedWorkOrder?.id === order?.id
                          ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <span className="font-semibold text-foreground">{order?.folioOt}</span>
                        {selectedWorkOrder?.id === order?.id && (
                          <Icon name="CheckCircle" size={18} className="text-primary" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-1">
                        {order?.vehicle?.marca} {order?.vehicle?.modelo}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Patente: {order?.vehicle?.patente}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Satisfaction Rating */}
            {selectedWorkOrder && (
              <div className="bg-card rounded-lg shadow-elevation-2 p-6 mb-6">
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="Star" size={20} className="text-primary" />
                  Calificación de Satisfacción
                </h2>

                <div className="text-center py-6">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    {[1, 2, 3, 4, 5]?.map((star) => (
                      <button
                        key={star}
                        onClick={() => handleStarClick(star)}
                        onMouseEnter={() => handleStarHover(star)}
                        onMouseLeave={handleStarLeave}
                        className="transition-smooth hover:scale-110 press-effect focus-ring rounded"
                      >
                        <Icon
                          name="Star"
                          size={48}
                          className={`${
                            star <= displayRating
                              ? 'text-warning fill-warning' :'text-muted-foreground'
                          } transition-smooth`}
                        />
                      </button>
                    ))}
                  </div>
                  {displayRating > 0 && (
                    <p className="text-lg font-medium text-foreground">
                      {getRatingLabel(displayRating)}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Service Comments */}
            {selectedWorkOrder && satisfactionRating > 0 && (
              <div className="bg-card rounded-lg shadow-elevation-2 p-6 mb-6">
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="MessageCircle" size={20} className="text-primary" />
                  Comentarios del Servicio
                </h2>

                <div className="space-y-2">
                  <textarea
                    value={serviceComments}
                    onChange={(e) => setServiceComments(e?.target?.value)}
                    placeholder="Comparta sus comentarios sobre el servicio recibido (opcional)..."
                    className="w-full h-32 px-4 py-3 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                    maxLength={1000}
                  />
                  <div className="flex justify-between items-center text-sm text-muted-foreground">
                    <p>Opcional - Ayúdenos a mejorar nuestro servicio</p>
                    <p>{characterCount}/1000</p>
                  </div>
                </div>
              </div>
            )}

            {/* Follow-up Scheduling */}
            {selectedWorkOrder && satisfactionRating > 0 && (
              <div className="bg-card rounded-lg shadow-elevation-2 p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-heading font-semibold text-foreground flex items-center gap-2">
                    <Icon name="Calendar" size={20} className="text-primary" />
                    Programar Seguimiento
                  </h2>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={followUpRequested}
                      onChange={(e) => setFollowUpRequested(e?.target?.checked)}
                      className="h-5 w-5 rounded border-input text-primary focus:ring-2 focus:ring-ring"
                    />
                    <span className="text-sm font-medium text-foreground">Solicitar seguimiento</span>
                  </label>
                </div>

                {followUpRequested && (
                  <div className="space-y-4 pt-4 border-t border-border">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        type="date"
                        label="Fecha de Seguimiento"
                        value={followUpDate}
                        onChange={(e) => setFollowUpDate(e?.target?.value)}
                        required
                        min={new Date()?.toISOString()?.split('T')?.[0]}
                      />
                      <Input
                        type="time"
                        label="Hora de Seguimiento"
                        value={followUpTime}
                        onChange={(e) => setFollowUpTime(e?.target?.value)}
                        required
                      />
                    </div>

                    <Input
                      type="text"
                      label="Tipo de Servicio Preferido"
                      value={preferredServiceType}
                      onChange={(e) => setPreferredServiceType(e?.target?.value)}
                      placeholder="Ej: Mantenimiento preventivo, Reparación específica..."
                    />

                    <div>
                      <label className="text-sm font-medium text-foreground mb-2 block">
                        Técnico Preferido (Opcional)
                      </label>
                      <select
                        value={preferredTechnicianId}
                        onChange={(e) => setPreferredTechnicianId(e?.target?.value)}
                        className="w-full h-10 px-3 py-2 rounded-md border border-input bg-background text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      >
                        <option value="">Sin preferencia</option>
                        {technicians?.map((tech) => (
                          <option key={tech?.id} value={tech?.id}>
                            {tech?.fullName}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Submit Button */}
            {selectedWorkOrder && satisfactionRating > 0 && (
              <div className="flex justify-end gap-4">
                <Button
                  variant="outline"
                  onClick={() => navigate('/workshop-dashboard')}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSubmit}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  iconName="Send"
                >
                  Enviar Feedback
                </Button>
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default CustomerFeedback;